import AppConstants from "../constants/AppConstants";
import { AssetProvider } from "../constants/AssetProviderConstants";

/**
 * @typedef {import("@/types/data/Pexels.mjs").PexelsPhoto} PexelsPhoto
 * @typedef {import("../constants/AssetProviderConstants").AssetProviderValue} AssetProviderValue
 */

const { RESOURCE_ASSET } = AppConstants;

const ASPECT_RATIO_PRECISION = 5;

/** The CDN's resize contract: compression plus a color space, alongside `w`. */
const PEXELS_RESIZE_PARAMS = { auto: "compress", cs: "tinysrgb" };

/**
 * Resizes through the Pexels CDN. Returns the url untouched for a width the CDN
 * cannot use, so callers can pass non-numeric ops like "file" or "preview".
 * @param {string} url
 * @param {number|string} [width]
 * @returns {string}
 */
export const getPexelsResizedUrl = (url, width) => {
  const numericWidth = Number(width);
  if (!Number.isFinite(numericWidth) || numericWidth <= 0) return url;

  const resized = new URL(url);
  Object.entries(PEXELS_RESIZE_PARAMS).forEach(([name, value]) => {
    resized.searchParams.set(name, value);
  });
  resized.searchParams.set("w", String(Math.round(numericWidth)));
  return resized.toString();
};

/**
 * Credit line required by Pexels' attribution guidelines.
 * @param {PexelsPhoto} photo
 * @returns {string}
 */
export const getPexelsCredit = (photo) =>
  photo?.photographer ? `Photo by ${photo.photographer} on Pexels` : "Photo from Pexels";

/**
 * Falls back to 1, never undefined: gallery renderers drop any image whose
 * aspect ratio is falsy, and only on the published page.
 * @param {PexelsPhoto} photo
 * @returns {number}
 */
export const getPexelsAspectRatio = (photo) =>
  photo?.width && photo?.height
    ? Number((photo.width / photo.height).toFixed(ASPECT_RATIO_PRECISION))
    : 1;

/**
 * Provenance persisted alongside the key, required by Pexels' attribution guidelines.
 * @param {PexelsPhoto} photo
 * @returns {{id: number, photographer: string, photographerUrl: string, url: string}}
 */
export const createPexelsAttribution = (photo) => ({
  id: photo.id,
  photographer: photo.photographer,
  photographerUrl: photo.photographerUrl,
  url: photo.url,
});

/**
 * Maps a photo to AssetUtils.getElementAsset-compatible content with an absolute key.
 * @param {PexelsPhoto} photo
 * @returns {{type: string, key: string, aspect_ratio: number, title: string, desc: string, provider: AssetProviderValue, attribution: Object}}
 */
export const createPexelsElementContent = (photo) => {
  const credit = getPexelsCredit(photo);

  return {
    type: RESOURCE_ASSET,
    key: photo.original,
    aspect_ratio: getPexelsAspectRatio(photo),
    title: photo.alt || credit,
    desc: photo.alt || credit,
    provider: AssetProvider.PEXELS,
    attribution: createPexelsAttribution(photo),
  };
};

/**
 * Maps a photo to the gallery shape; renderers require `ar`, not `aspect_ratio`,
 * and drop any item whose aspect ratio is falsy. Carries the same provenance as
 * element content: rendering picks the resize contract from `provider`, and
 * attribution has to survive in the saved data. GalleryElementContentSchema tolerates the
 * extra keys.
 * @param {PexelsPhoto} photo
 * @returns {{key: string, ar: number, title: string, desc: string, provider: AssetProviderValue, attribution: Object}}
 */
export const createPexelsGalleryItem = (photo) => {
  const credit = getPexelsCredit(photo);

  return {
    key: photo.original,
    ar: getPexelsAspectRatio(photo),
    title: photo.alt || credit,
    desc: photo.alt || credit,
    provider: AssetProvider.PEXELS,
    attribution: createPexelsAttribution(photo),
  };
};

const PexelsUtils = {
  getPexelsResizedUrl,
  createPexelsAttribution,
  createPexelsElementContent,
  createPexelsGalleryItem,
  getPexelsAspectRatio,
  getPexelsCredit,
};

export default PexelsUtils;
